import { useEffect } from "react";
import { useFirestoreData } from '../../hooks/useFirestoreData';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: appConfigData } = useFirestoreData('app_config');

  // Extract privacy_policy and contact_info from app_config
  const privacyPolicy = appConfigData.find(doc => doc.id === 'privacy_policy') || {};
  const contactInfo = appConfigData.find(doc => doc.id === 'contact_info') || {};

  // Use Firestore data if available and active, otherwise show static content
  const hasDynamicContent = privacyPolicy.isActive && privacyPolicy.sections && privacyPolicy.sections.length > 0;
  const title = privacyPolicy.title || 'Privacy Policy';
  const version = privacyPolicy.version;
  const sections = privacyPolicy.sections || [];
  const contactEmail = contactInfo.contactEmail || 'legal@alldoer.com';
  const address = contactInfo.addressLine1 && contactInfo.addressLine2
    ? `${contactInfo.addressLine1}, ${contactInfo.addressLine2}`
    : contactInfo.addressLine1 || contactInfo.addressLine2 || 'Indore, Madhya Pradesh, India';

  return (
    <div className="w-full bg-white pt-28 pb-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 border-b border-gray-100 ">
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6">
            {title}
          </h1>
          {version && (
            <p className="text-sm text-gray-500 mb-6">Version {version}</p>
          )}
        </div>

        {/* Content */}
        <div className="space-y-12 text-gray-600 leading-relaxed text-base md:text-lg">
          {hasDynamicContent ? (
            /* Dynamic Content from Firestore */
            sections.map((section, index) => (
              <section key={index}>
                {section.heading && (
                  <h2 className="text-2xl font-medium text-gray-900 mb-4">
                    {section.heading}
                  </h2>
                )}
                <div
                  className="whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: section.body }}
                />
              </section>
            ))
          ) : (
            /* Static Fallback Content */
            <>
              {/* Introduction */}
              <section>
                <p>
                  Welcome to Alldoer. We respect your privacy and are committed to
                  protecting your personal data. This privacy policy will inform you
                  as to how we look after your personal data when you visit our
                  website (regardless of where you visit it from) and tell you about
                  your privacy rights and how the law protects you.
                </p>
              </section>

              {/* Section 1 */}
              <section>
                <h2 className="text-2xl font-medium text-gray-900 mb-4">
                  1. Information We Collect
                </h2>
                <p className="mb-4">
                  We may collect, use, store, and transfer different kinds of
                  personal data about you which we have grouped together as follows:
                </p>
                <ul className="list-disc pl-6 space-y-3 marker:text-gray-400">
                  <li>
                    <strong className="text-gray-900">Identity Data:</strong>{" "}
                    Includes first name, last name, username or similar identifier,
                    and title.
                  </li>
                  <li>
                    <strong className="text-gray-900">Contact Data:</strong>{" "}
                    Includes billing address, delivery address, email address, and
                    telephone numbers.
                  </li>
                  <li>
                    <strong className="text-gray-900">Transaction Data:</strong>{" "}
                    Includes details about payments to and from you and other
                    details of products and sourcing services you have purchased
                    from us.
                  </li>
                  <li>
                    <strong className="text-gray-900">Technical Data:</strong>{" "}
                    Includes internet protocol (IP) address, your login data,
                    browser type and version, time zone setting and location,
                    browser plug-in types and versions, operating system and
                    platform, and other technology on the devices you use to access
                    this website.
                  </li>
                </ul>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-2xl font-medium text-gray-900 mb-4">
                  2. How We Use Your Data
                </h2>
                <p className="mb-4">
                  We will only use your personal data when the law allows us to.
                  Most commonly, we will use your personal data in the following
                  circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-3 marker:text-gray-400">
                  <li>
                    Where we need to perform the contract we are about to enter into
                    or have entered into with you (e.g., fulfilling a sourcing
                    order).
                  </li>
                  <li>
                    Where it is necessary for our legitimate interests (or those of
                    a third party) and your interests and fundamental rights do not
                    override those interests.
                  </li>
                  <li>Where we need to comply with a legal obligation.</li>
                </ul>
              </section>
            </>
          )}

          {/* Contact Section */}
          <section className="bg-gray-50 rounded-2xl p-8 border border-gray-100 mt-12">
            <h2 className="text-2xl font-medium text-gray-900 mb-4">
              Contact Us
            </h2>
            <p className="mb-6 text-sm">
              If you have any questions about this privacy policy or our privacy
              practices, please contact us at:
            </p>
            <div className="space-y-2 text-gray-900 font-medium">
              <p>
                Email:{" "}
                <a
                  href={`mailto:${contactEmail}`}
                  className="hover:text-red-600 transition-colors"
                >
                  {contactEmail}
                </a>
              </p>
              <p>Address: {address}</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
